import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { favoritesService } from '../../services';

/**
 * @file favoritesSlice.js
 * @description Redux slice for managing user favorites (artworks).
 *
 * NAMING CONVENTION:
 * - Slice name: "favorites" (aligned with DB table + service)
 * - State: state.favorites.favorite_items, favorite_status, loading_favorites, error
 * - Thunks: fetchFavorites, addToFavoritesAsync, removeFromFavoritesAsync, checkIsFavoriteAsync
 * - Reducers: addToFavoritesLocal, removeFromFavoritesLocal, clearFavoritesError, etc.
 *
 * MIGRATION FROM wishlistSlice:
 * - This slice replaces the deprecated wishlistSlice.js
 * - See docs/FAVORITES_MIGRATION_GUIDE.md for migration steps
 * - wishlistSlice.js exports alias for backward compatibility (temporary)
 *
 * ARCHITECTURE:
 * - Supabase direct integration (no Backend Custom API)
 * - Optimistic UI updates via local reducers
 * - Async thunks for DB sync
 */

// ============================================
// ASYNC THUNKS
// ============================================

/**
 * Fetch user's favorite artworks from Supabase
 * NOTE: During Phase A-B, this thunk is named 'fetchWishlist' for backward compatibility
 * @param {string} user_id - User UUID (or userId for backward compatibility)
 * @returns {Array} Array of favorite items with artwork data
 */
export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',    // NOTE: Using old action type for backward compatibility (TODO: change to 'favorites/fetchFavorites' in Phase C)
  async (user_id, { rejectWithValue }) => {
    try {
      const { data, error } = await favoritesService.getUserFavorites(user_id);
      if (error) throw new Error(error);
      return data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Add artwork to user's favorites (async - persists to DB)
 * NOTE: During Phase A-B, this thunk is named 'addToWishlistAsync' for backward compatibility
 * @param {Object} params
 * @param {string} params.user_id - User UUID (or userId for backward compatibility)
 * @param {string} params.artwork_id - Artwork UUID
 * @returns {Array} Updated favorites array
 */
export const addToWishlistAsync = createAsyncThunk(
  'wishlist/addToWishlistAsync',    // NOTE: Using old action type for backward compatibility (TODO: change to 'favorites/addToFavoritesAsync' in Phase C)
  async ({ user_id, userId, artwork_id }, { rejectWithValue }) => {
    try {
      const final_user_id = user_id || userId; // Accept both for backward compatibility
      const { data, error } = await favoritesService.addToFavorites(final_user_id, artwork_id);
      if (error) throw new Error(error);

      // Fetch updated favorites to keep everything in sync
      const { data: updated_favorites, error: fetch_error } = await favoritesService.getUserFavorites(final_user_id);
      if (fetch_error) throw new Error(fetch_error);

      return updated_favorites || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Remove artwork from user's favorites (async - persists to DB)
 * NOTE: During Phase A-B, this thunk is named 'removeFromWishlistAsync' for backward compatibility
 * @param {Object} params
 * @param {string} params.user_id - User UUID (or userId for backward compatibility)
 * @param {string} params.artwork_id - Artwork UUID
 * @returns {Array} Updated favorites array
 */
export const removeFromWishlistAsync = createAsyncThunk(
  'wishlist/removeFromWishlistAsync',    // NOTE: Using old action type for backward compatibility (TODO: change to 'favorites/removeFromFavoritesAsync' in Phase C)
  async ({ user_id, userId, artwork_id }, { rejectWithValue }) => {
    try {
      const final_user_id = user_id || userId; // Accept both for backward compatibility
      const { error } = await favoritesService.removeFromFavorites(final_user_id, artwork_id);
      if (error) throw new Error(error);

      // Fetch updated favorites to keep everything in sync
      const { data: updated_favorites, error: fetch_error } = await favoritesService.getUserFavorites(final_user_id);
      if (fetch_error) throw new Error(fetch_error);

      return updated_favorites || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Check if artwork is in user's favorites (async - queries DB)
 * NOTE: During Phase A-B, this thunk is named 'checkIsFavorite' for backward compatibility
 * @param {Object} params
 * @param {string} params.user_id - User UUID (or userId for backward compatibility)
 * @param {string} params.artwork_id - Artwork UUID
 * @returns {Object} { artwork_id, is_favorite } or { artwork_id, isFavorite } for backward compatibility
 */
export const checkIsFavorite = createAsyncThunk(
  'wishlist/checkIsFavorite',    // NOTE: Using old action type for backward compatibility (TODO: change to 'favorites/checkIsFavoriteAsync' in Phase C)
  async ({ user_id, userId, artwork_id }, { rejectWithValue }) => {
    try {
      const final_user_id = user_id || userId; // Accept both for backward compatibility
      const { isFavorite, error } = await favoritesService.isFavorite(final_user_id, artwork_id);
      if (error) throw new Error(error);
      return { artwork_id, isFavorite }; // Keep old format for backward compatibility
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================
// MODERN ALIASES (for new code)
// ============================================

/**
 * Modern alias: fetchFavorites
 * Use this in new code for consistency with naming convention
 */
export const fetchFavorites = fetchWishlist;

/**
 * Modern alias: addToFavoritesAsync
 * Use this in new code for consistency with naming convention
 */
export const addToFavoritesAsync = addToWishlistAsync;

/**
 * Modern alias: removeFromFavoritesAsync
 * Use this in new code for consistency with naming convention
 */
export const removeFromFavoritesAsync = removeFromWishlistAsync;

/**
 * Modern alias: checkIsFavoriteAsync
 * Use this in new code for consistency with naming convention
 */
export const checkIsFavoriteAsync = checkIsFavorite;

// ============================================
// INITIAL STATE
// ============================================

/**
 * NOTE: During migration Phase A-B, we use "items" instead of "favorite_items"
 * and "favoriteStatus" instead of "favorite_status" to maintain backward compatibility.
 * In Phase C (Session 8), we can rename to more descriptive names.
 *
 * BACKWARD COMPATIBILITY:
 * - slice name: 'wishlist' (TODO: rename to 'favorites' in Phase C)
 * - This ensures Redux action types have 'wishlist/' prefix during migration
 * - Tests and existing code expecting 'wishlist/...' actions will continue working
 */
const initialState = {
  items: [],                    // Array of favorite items with artwork data (TODO: rename to favorite_items in Phase C)
  favoriteStatus: {},           // Object to track favorite status for individual artworks { artwork_id: true/false } (TODO: rename to favorite_status in Phase C)
  loading: false,               // Loading state for async operations (TODO: rename to loading_favorites in Phase C)
  error: null                   // Error message (string or null)
};

// ============================================
// SLICE DEFINITION
// ============================================

export const favoritesSlice = createSlice({
  name: 'wishlist',             // NOTE: Using 'wishlist' for backward compatibility during Phase A-B (TODO: rename to 'favorites' in Phase C)
  initialState,
  reducers: {
    /**
     * Add favorite locally (optimistic UI update - NO DB persistence)
     * Use for offline functionality or optimistic updates
     */
    addToFavoritesLocal: (state, action) => {
      const exists = state.items.find(item =>
        (item.artwork?.id || item.id) === (action.payload.artwork?.id || action.payload.id)
      );
      if (!exists) {
        state.items.push(action.payload);
      }
    },

    /**
     * Remove favorite locally (optimistic UI update - NO DB persistence)
     * Use for offline functionality or optimistic updates
     */
    removeFromFavoritesLocal: (state, action) => {
      const { id } = action.payload;
      state.items = state.items.filter(item =>
        (item.artwork?.id || item.id) !== id
      );
    },

    /**
     * Clear error state
     */
    clearFavoritesError: (state) => {
      state.error = null;
    },

    /**
     * Set favorite status for a specific artwork
     * Used for quick UI updates without fetching entire favorites list
     */
    setFavoriteStatus: (state, action) => {
      // NOTE: Backward compatibility - accepting both is_favorite and isFavorite
      const { artwork_id, is_favorite, isFavorite } = action.payload;
      state.favoriteStatus[artwork_id] = is_favorite !== undefined ? is_favorite : isFavorite;
    },

    /**
     * Clear all favorites (local state only - does NOT delete from DB)
     * Used on logout or session reset
     */
    clearFavorites: (state) => {
      state.items = [];
      state.favoriteStatus = {};
      state.loading = false;
      state.error = null;
    }
  },

  // ============================================
  // EXTRA REDUCERS (Async Thunks)
  // ============================================

  extraReducers: (builder) => {
    builder
      // -------------------- fetchWishlist (fetchFavorites alias) --------------------
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;

        // Update favoriteStatus for all items
        state.favoriteStatus = {};
        action.payload.forEach(item => {
          const artwork_id = item.artwork?.id || item.artwork_id;
          if (artwork_id) {
            state.favoriteStatus[artwork_id] = true;
          }
        });
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // -------------------- addToWishlistAsync (addToFavoritesAsync alias) --------------------
      .addCase(addToWishlistAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToWishlistAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;

        // Update favoriteStatus for all items
        state.favoriteStatus = {};
        action.payload.forEach(item => {
          const artwork_id = item.artwork?.id || item.artwork_id;
          if (artwork_id) {
            state.favoriteStatus[artwork_id] = true;
          }
        });
      })
      .addCase(addToWishlistAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // -------------------- removeFromWishlistAsync (removeFromFavoritesAsync alias) --------------------
      .addCase(removeFromWishlistAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromWishlistAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;

        // Update favoriteStatus for all remaining items
        state.favoriteStatus = {};
        action.payload.forEach(item => {
          const artwork_id = item.artwork?.id || item.artwork_id;
          if (artwork_id) {
            state.favoriteStatus[artwork_id] = true;
          }
        });
      })
      .addCase(removeFromWishlistAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // -------------------- checkIsFavorite (checkIsFavoriteAsync alias) --------------------
      .addCase(checkIsFavorite.fulfilled, (state, action) => {
        const { artwork_id, isFavorite } = action.payload;
        state.favoriteStatus[artwork_id] = isFavorite;
      });
  }
});

// ============================================
// EXPORTS
// ============================================

// ============================================
// ACTIONS EXPORTS
// ============================================

// Backward compatibility exports (old names)
export const {
  addToFavoritesLocal: addToWishlistLocal,
  removeFromFavoritesLocal: removeFromWishlistLocal,
  clearFavoritesError: clearError,
  clearFavorites: clearWishlist,
  setFavoriteStatus
} = favoritesSlice.actions;

// Modern exports (new names - for new code)
export const {
  addToFavoritesLocal,
  removeFromFavoritesLocal,
  clearFavoritesError,
  clearFavorites
} = favoritesSlice.actions;

// Export reducer
export default favoritesSlice.reducer;
